import { useParams, Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { usePostBySlug } from "@/hooks/usePosts";
import { Helmet } from "react-helmet-async";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, ArrowLeft, User } from "lucide-react";
import { format } from "date-fns";
import ReactMarkdown from "react-markdown";
import SocialShare from "@/components/blog/SocialShare";
import { Skeleton } from "@/components/ui/skeleton";

const categoryLabels: Record<string, string> = {
  fundraising: "Fundraising",
  "community-service": "Community Service",
  collaboration: "Collaboration",
  education: "Education",
  other: "Community Impact",
};

const StoryDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data: post, isLoading } = usePostBySlug(slug);

  const url = typeof window !== "undefined"
    ? `${window.location.origin}/impact/stories/${slug}`
    : "";

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <main className="container mx-auto px-4 pt-32 pb-16 max-w-3xl">
          <Skeleton className="h-12 w-3/4 mb-4" />
          <Skeleton className="h-64 w-full mb-6" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-2/3" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <main className="container mx-auto px-4 pt-32 pb-16 text-center flex-1">
          <h1 className="text-3xl font-bold mb-4">Story not found</h1>
          <Button asChild variant="outline">
            <Link to="/impact/stories">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Stories
            </Link>
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  const description = post.excerpt || post.title;

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{post.title} | RCN Impact Stories</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        {post.cover_image && <meta property="og:image" content={post.cover_image} />}
        {post.published_at && (
          <meta property="article:published_time" content={post.published_at} />
        )}

        {/* Twitter */}
        <meta name="twitter:card" content={post.cover_image ? "summary_large_image" : "summary"} />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={description} />
        {post.cover_image && <meta name="twitter:image" content={post.cover_image} />}

        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description,
            image: post.cover_image || undefined,
            datePublished: post.published_at,
            author: post.author?.name
              ? { "@type": "Person", name: post.author.name }
              : undefined,
            publisher: {
              "@type": "Organization",
              name: "Referral Connection Network",
            },
          })}
        </script>
      </Helmet>

      <Navigation />

      <article className="flex-1 pt-28 pb-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <Button asChild variant="ghost" size="sm" className="mb-6 -ml-3">
            <Link to="/impact/stories">
              <ArrowLeft className="h-4 w-4 mr-2" /> All Stories
            </Link>
          </Button>

          <Badge className="mb-3" variant="secondary">
            {categoryLabels[post.category] ?? post.category}
          </Badge>

          <h1 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-8">
            {post.published_at && (
              <span className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                {format(new Date(post.published_at), "MMMM d, yyyy")}
              </span>
            )}
            {post.author?.name && (
              <span className="flex items-center gap-1.5">
                <User className="h-4 w-4" />
                {post.author.name}
              </span>
            )}
          </div>

          {post.cover_image && (
            <img
              src={post.cover_image}
              alt={post.title}
              className="w-full rounded-lg shadow-lg mb-8 aspect-video object-cover"
            />
          )}

          <div className="prose prose-lg dark:prose-invert max-w-none prose-headings:font-bold prose-img:rounded-lg prose-img:shadow-md prose-a:text-primary">
            <ReactMarkdown>{post.body_markdown}</ReactMarkdown>
          </div>

          <div className="mt-12 pt-8 border-t">
            <SocialShare url={url} title={post.title} excerpt={post.excerpt ?? undefined} />
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default StoryDetail;
